import Api from '@/misc/api'
import type { Commit, FalseJustificationItem, FlagJustificationPayload, ListFalseJustificationsResponse, PreviouslyDisplayedMcoStay, PreviouslyDisplayedSmrStay } from '@/misc/types'
import type { RawMcoStay } from '@/store/modules/stay/mco/types'
import type { RawSmrStay } from '@/store/modules/stay/smr/types'

// Routed through Api.get (instead of a bare fetch) so a 401 triggers the same session-expiry
// retry, and a failure surfaces the same error toast, as every other API call in the app.
export function fetchFalseJustifications(url: string, commit: Commit): Promise<ListFalseJustificationsResponse> {
  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListFalseJustificationsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay false justifications (${url})`)),
      },
      commit
    )
  })
}

// Same backend route as store.js's createFalseJustificationForSuggestion, called as a plain function
// since that action/its mutations are hard-wired to state.displayedStay — unusable from the aside's
// quick view, which never loads the full stay. Caller must refresh its own false-justifications list.
export function createFalseJustification(
  stayId: number,
  payload: FlagJustificationPayload,
  commit: Commit
): Promise<void> {
  return new Promise((resolve, reject) => {
    Api.post(
      {
        url: `/api/mco/stay/${stayId}/false-justification`,
        body: payload,
        successMut: () => resolve(),
        errorMut: () => reject(new Error('Failed to create false justification')),
      },
      commit
    )
  })
}

export function deleteFalseJustification(justificationId: number, commit: Commit): Promise<void> {
  return new Promise((resolve, reject) => {
    Api.del(
      {
        url: `/api/false-justification/${justificationId}`,
        successMut: () => resolve(),
        errorMut: () => reject(new Error('Failed to delete false justification')),
      },
      commit
    )
  })
}

/**
 * Kicks off the false-justifications fetch alongside the stay/documents/justifications requests, so
 * it can run in parallel with (rather than only after) the stay request.
 */
export function prefetchMcoStayFalseJustifications(idType: string, stayId: string, commit: Commit): Promise<ListFalseJustificationsResponse> {
  return fetchFalseJustifications(`/api/stay/${stayId}/false-justifications?idType=${idType}`, commit)
}

export function prefetchSmrStayFalseJustifications(idType: string, stayId: string, commit: Commit): Promise<ListFalseJustificationsResponse> {
  return fetchFalseJustifications(`/api/smr/stay/${stayId}/false-justifications?idType=${idType}`, commit)
}

// False justifications don't change as a result of coding a stay (diagnosis/status changes) — only
// flag/unflag actions do, and those already mutate state.displayedStay.falseJustifications in place
// (see createFalseJustificationsSuccess/deleteFalseJustificationsSuccess) — so this is reused from the
// previously displayed stay on every other refresh, mirroring getSuggestionJustifications.
export async function getFalseJustifications(
  stay: RawMcoStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedMcoStay | null,
  prefetched: Promise<ListFalseJustificationsResponse> | null
): Promise<FalseJustificationItem[]> {
  if (previousStay?.id === stay.id) {
    return previousStay.falseJustifications ?? []
  }

  try {
    const response = await (prefetched ?? fetchFalseJustifications(`/api/stay/${stay.id}/false-justifications?idType=id`, commit))

    return response.falseJustifications
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return []
  }
}

export async function getSmrFalseJustifications(
  stay: RawSmrStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedSmrStay | null,
  prefetched: Promise<ListFalseJustificationsResponse> | null
): Promise<FalseJustificationItem[]> {
  if (previousStay?.id === stay.id) {
    return previousStay.falseJustifications ?? []
  }

  try {
    const response = await (prefetched ?? fetchFalseJustifications(`/api/smr/stay/${stay.id}/false-justifications?idType=id`, commit))

    return response.falseJustifications
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return []
  }
}
